import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/*
 * HeroSlideshow, full-width auto-advancing crossfade slideshow.
 *  Full-image display: object-contain with a blurred, enlarged copy
 *  of the same image behind it to fill empty side/top areas.
 *  Props:
 *   slides:      [{ src, alt?, caption? }] , caption is ignored (removed by spec)
 *   intervalMs:  auto-advance interval (default 5000)
 */
const HeroSlideshow = ({ slides = [], intervalMs = 5000 }) => {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const containerRef = useRef(null);
    const touchStartX = useRef(null);
    const touchDeltaX = useRef(0);

    const total = slides.length;

    const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
    const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);
    const goTo = (i) => setIndex(((i % total) + total) % total);

    // Auto-advance
    useEffect(() => {
        if (paused || total < 2) return undefined;
        const t = setInterval(() => setIndex((i) => (i + 1) % total), intervalMs);
        return () => clearInterval(t);
    }, [paused, total, intervalMs]);

    // Keyboard nav
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const onKey = (e) => {
            if (!el.matches(":hover") && document.activeElement !== el) return;
            if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [next, prev]);

    // Touch / swipe
    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        touchDeltaX.current = 0;
    };
    const onTouchMove = (e) => {
        if (touchStartX.current == null) return;
        touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
    };
    const onTouchEnd = () => {
        const dx = touchDeltaX.current;
        if (Math.abs(dx) > 45) {
            if (dx < 0) next();
            else prev();
        }
        touchStartX.current = null;
        touchDeltaX.current = 0;
    };

    if (total === 0) return null;

    return (
        <div
            ref={containerRef}
            role="region"
            aria-roledescription="carousel"
            aria-label="Parish photo slideshow"
            data-testid="hero-slideshow"
            tabIndex={-1}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            className="relative overflow-hidden w-full h-[62vh] sm:h-[70vh] lg:h-[82vh] min-h-[420px] select-none"
            style={{ background: "#2e2220" }}
        >
            {slides.map((s, i) => {
                const active = i === index;
                return (
                    <div
                        key={s.src}
                        aria-hidden={!active}
                        className="absolute inset-0"
                        style={{
                            opacity: active ? 1 : 0,
                            transitionProperty: "opacity",
                            transitionDuration: "1200ms",
                            transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                    >
                        {/* Blurred, enlarged backdrop of the same image */}
                        <img
                            src={s.src}
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 h-full w-full object-cover"
                            style={{
                                filter: "blur(28px) saturate(120%)",
                                transform: "scale(1.15)",
                                opacity: 0.65,
                            }}
                            loading={i === 0 ? "eager" : "lazy"}
                            decoding="async"
                            draggable={false}
                        />
                        {/* Full image on top, contained (no crop) */}
                        <img
                            src={s.src}
                            alt={s.alt || `Slide ${i + 1}`}
                            className="absolute inset-0 h-full w-full object-contain"
                            loading={i === 0 ? "eager" : "lazy"}
                            decoding="async"
                            draggable={false}
                        />
                        {/* Very subtle darkening from the very bottom only, so the dots/arrows stay legible.
                            No overlay across the image itself, photos display in full colour. */}
                        <div
                            className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
                            style={{
                                background:
                                    "linear-gradient(180deg, rgba(46,34,32,0) 0%, rgba(46,34,32,0.55) 100%)",
                            }}
                        />
                    </div>
                );
            })}

            {/* Prev / Next controls */}
            {total > 1 && (
                <>
                    <button
                        type="button"
                        onClick={prev}
                        data-testid="hero-slideshow-prev"
                        aria-label="Previous slide"
                        className="absolute left-3 sm:left-5 bottom-16 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 z-20 flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-full transition-all hover:scale-105"
                        style={{
                            background: "rgba(46,34,32,0.65)",
                            border: "1px solid rgba(201,162,39,0.55)",
                            color: "#F3EDE4",
                            backdropFilter: "blur(6px)",
                        }}
                    >
                        <ChevronLeft size={22} />
                    </button>
                    <button
                        type="button"
                        onClick={next}
                        data-testid="hero-slideshow-next"
                        aria-label="Next slide"
                        className="absolute right-3 sm:right-5 bottom-16 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 z-20 flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-full transition-all hover:scale-105"
                        style={{
                            background: "rgba(46,34,32,0.65)",
                            border: "1px solid rgba(201,162,39,0.55)",
                            color: "#F3EDE4",
                            backdropFilter: "blur(6px)",
                        }}
                    >
                        <ChevronRight size={22} />
                    </button>

                    {/* Dot indicators */}
                    <div
                        data-testid="hero-slideshow-dots"
                        className="absolute bottom-6 sm:bottom-8 left-0 right-0 z-20 flex items-center justify-center gap-2"
                    >
                        {slides.map((s, i) => (
                            <button
                                key={s.src}
                                type="button"
                                onClick={() => goTo(i)}
                                aria-label={`Go to slide ${i + 1}`}
                                aria-current={i === index}
                                data-testid={`hero-slideshow-dot-${i}`}
                                className="h-2 rounded-full transition-all"
                                style={{
                                    width: i === index ? 28 : 10,
                                    background: i === index ? "#C9A227" : "rgba(243,237,228,0.55)",
                                    boxShadow: i === index ? "0 0 12px rgba(201,162,39,0.65)" : "none",
                                }}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default HeroSlideshow;
